import {
  Icon,
  Menu,
  MenuDivider,
  MenuItem,
  showContextMenu,
  Tag,
  Tree,
  type TreeNodeInfo,
} from '@blueprintjs/core'
import type { IconName } from '@blueprintjs/icons'

import type { Operation, Store } from '../store'

/**
 * SolidWorks-style FeatureManager tree.
 *
 * Reads the job from the store and derives a Blueprint TreeNodeInfo
 * tree. Status icons come from the dependency graph in the store
 * (`regenState` on each operation).
 *
 * Selecting a tree node sets `store.selection`, which the viewport and
 * PropertyManager both read; the reverse (viewport pick → tree) is
 * wired in Phase 4.
 */
export function FeatureTreePanel({ store }: { store: Store }) {
  const { job, selection } = store

  const nodes: TreeNodeInfo[] = [
    {
      id: 'part',
      icon: 'cube',
      label: (
        <span>
          Part <TreeDim>{job.part?.filename ?? '— no STEP'}</TreeDim>
        </span>
      ),
      isSelected: selection?.kind === 'part',
      disabled: !job.part,
    },
    {
      id: 'cell',
      icon: 'build',
      label: (
        <span>
          Cell <TreeDim>{job.cell.robot ?? 'IRB 4600 + positioner'}</TreeDim>
        </span>
      ),
      isSelected: selection?.kind === 'cell',
      hasCaret: true,
      isExpanded: true,
      childNodes: [
        {
          id: 'cell/tool',
          icon: 'wrench',
          label: (
            <span>
              Tool{' '}
              <TreeDim>
                {job.cell.tool ? `${job.cell.tool.vendor} ${job.cell.tool.part}` : '— none'}
              </TreeDim>
            </span>
          ),
          isSelected: selection?.kind === 'tool',
        },
      ],
    },
    {
      id: 'wobj',
      icon: 'map-marker',
      label: (
        <span>
          Work object <TreeDim>world</TreeDim>
        </span>
      ),
      isSelected: selection?.kind === 'wobj',
    },
    {
      id: 'operations',
      icon: 'layers',
      label: <span>Operations</span>,
      hasCaret: true,
      isExpanded: true,
      childNodes: job.operations.map(opToNode(selection)),
    },
    {
      id: 'outputs',
      icon: 'export',
      label: <span>Outputs</span>,
      hasCaret: true,
      isExpanded: true,
      childNodes: [
        {
          id: 'outputs/toolpath',
          icon: 'document',
          label: 'toolpath.json',
          disabled: !store.hasAnyToolpath,
        },
        {
          id: 'outputs/trajectory',
          icon: 'document',
          label: 'trajectory.json',
          disabled: !store.hasAnyTrajectory,
        },
        {
          id: 'outputs/rapid',
          icon: 'document',
          label: 'rapid.mod',
          disabled: !store.hasAnyTrajectory,
        },
      ],
    },
  ]

  return (
    <div className="feature-tree">
      <div className="feature-tree-header">
        <Icon icon="projects" />
        <span>Job: {store.job.name}</span>
      </div>
      <Tree
        contents={nodes}
        onNodeClick={(n) => store.selectTreeNode(n.id as string)}
        onNodeExpand={() => {}}
        onNodeCollapse={() => {}}
        onNodeContextMenu={(n, _p, e) => {
          e.preventDefault()
          if (typeof n.id === 'string' && n.id.startsWith('operations/')) {
            const opId = n.id.slice('operations/'.length)
            showContextMenu({
              content: <OperationMenu opId={opId} store={store} />,
              targetOffset: { left: e.clientX, top: e.clientY },
            })
          }
        }}
      />
    </div>
  )
}

function OperationMenu({ opId, store }: { opId: string; store: Store }) {
  return (
    <Menu>
      <MenuItem icon="edit" text="Edit…" onClick={() => store.selectTreeNode(`operations/${opId}`)} />
      <MenuItem icon="refresh" text="Regenerate" onClick={() => store.generateOne(opId)} />
      <MenuDivider />
      <MenuItem icon="duplicate" text="Duplicate" onClick={() => store.duplicateOperation(opId)} />
      <MenuItem icon="disable" text="Suppress" onClick={() => store.toggleSuppress(opId)} />
      <MenuDivider />
      <MenuItem icon="trash" text="Delete" intent="danger" onClick={() => store.deleteOperation(opId)} />
    </Menu>
  )
}

function TreeDim({ children }: { children: React.ReactNode }) {
  return <span className="tree-dim">{children}</span>
}

function opToNode(
  selection: Store['selection'],
): (op: Operation) => TreeNodeInfo {
  return (op) => {
    const { icon, intent } = STATE_BADGE[op.regenState]
    return {
      id: `operations/${op.id}`,
      icon: op.kind === 'clad' ? 'layers' : 'merge-links',
      label: (
        <span>
          {op.label}{' '}
          <Tag minimal intent={intent} icon={icon as IconName}>
            {op.regenState}
          </Tag>
        </span>
      ),
      isSelected:
        selection?.kind === 'operation' && selection.id === op.id,
      secondaryLabel: op.suppressed ? <Tag minimal>supp</Tag> : undefined,
    }
  }
}

const STATE_BADGE: Record<
  Operation['regenState'],
  { icon: IconName; intent: 'none' | 'success' | 'warning' | 'danger' | 'primary' }
> = {
  ok: { icon: 'tick', intent: 'success' },
  needsRegen: { icon: 'refresh', intent: 'warning' },
  running: { icon: 'time', intent: 'primary' },
  error: { icon: 'cross', intent: 'danger' },
}
